"use client";

import * as PopoverPrimitive from "@radix-ui/react-popover";
import React from "react";
import { splitBreakpointProps } from "@/lib/utils";
import type { PopoverContextType, PopoverProps } from "../types";

const DEFAULT_CLOSE_DELAY = 100;
const DEFAULT_OPEN_DELAY = 300;

export const PopoverContext = React.createContext<
  PopoverContextType | undefined
>(undefined);

export const usePopover = (): PopoverContextType => {
  const context = React.useContext(PopoverContext);
  if (!context) {
    throw new Error("usePopover must be used within a Popover");
  }
  return context;
};

function Popover({
  open,
  defaultOpen,
  onOpenChange,
  closeDelay = DEFAULT_CLOSE_DELAY,
  openDelay = DEFAULT_OPEN_DELAY,
  ...props
}: PopoverProps) {
  const [isOpen, setIsOpen] = React.useState(open ?? defaultOpen ?? false);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const closeTimer = React.useRef<number | null>(null);
  const openTimer = React.useRef<number | null>(null);
  const { native } = splitBreakpointProps(props);

  const handleOnOpenChange = React.useCallback(
    (open: boolean) => {
      setIsOpen(open);

      if (onOpenChange) {
        onOpenChange(open);
      }
    },
    [onOpenChange],
  );

  const clearCloseTimer = React.useCallback(() => {
    if (!closeTimer.current) {
      return;
    }

    clearTimeout(closeTimer.current);
    closeTimer.current = null;
  }, []);

  const clearOpenTimer = React.useCallback(() => {
    if (!openTimer.current) {
      return;
    }

    clearTimeout(openTimer.current);
    openTimer.current = null;
  }, []);

  const clearTimers = React.useCallback(() => {
    clearCloseTimer();
    clearOpenTimer();
  }, [clearCloseTimer, clearOpenTimer]);

  const startCloseTimer = React.useCallback(() => {
    clearCloseTimer();

    closeTimer.current = window.setTimeout(() => {
      setIsOpen(false);
      triggerRef.current?.blur();
      contentRef.current?.blur();
    }, closeDelay);
  }, [clearCloseTimer, closeDelay]);

  const startOpenTimer = React.useCallback(() => {
    if (open) {
      return;
    }

    clearTimers();

    openTimer.current = window.setTimeout(() => {
      setIsOpen(true);
    }, openDelay);
  }, [open, clearTimers, openDelay]);

  const getTriggerHoverProps = React.useCallback(() => {
    return {
      ref: (node: HTMLButtonElement | null) => {
        triggerRef.current = node;
      },
      onMouseEnter: () => {
        clearCloseTimer();
        startOpenTimer();
      },
      onMouseLeave: (event: React.MouseEvent) => {
        if (!contentRef.current || !triggerRef.current) {
          return;
        }

        const next = event.relatedTarget as Node | null;

        if (
          next &&
          (triggerRef.current?.contains(next) ||
            contentRef.current?.contains(next))
        ) {
          return;
        }

        clearOpenTimer();
        startCloseTimer();
      },
      onFocus: () => {
        clearTimers();
        setIsOpen(true);
      },
      onBlur: (event: React.FocusEvent) => {
        if (!contentRef.current || !triggerRef.current) {
          return;
        }

        const next = event.relatedTarget as Node | null;

        if (
          next &&
          (triggerRef.current.contains(next) ||
            contentRef.current.contains(next))
        ) {
          return;
        }

        clearTimers();
        setIsOpen(false);
      },
    } as const;
  }, [
    startCloseTimer,
    clearCloseTimer,
    clearOpenTimer,
    clearTimers,
    startOpenTimer,
  ]);

  const getContentHoverProps = React.useCallback(() => {
    return {
      ref: (node: HTMLDivElement | null) => {
        contentRef.current = node;
      },
      onMouseEnter: () => {
        clearCloseTimer();
      },
      onMouseLeave: (event: React.MouseEvent) => {
        if (!contentRef.current || !triggerRef.current) {
          return;
        }

        const next = event.relatedTarget as Node | null;

        if (
          next &&
          (contentRef.current.contains(next) ||
            triggerRef.current.contains(next))
        ) {
          return;
        }

        startCloseTimer();
      },
    } as const;
  }, [clearCloseTimer, startCloseTimer]);

  React.useEffect(() => {
    if (open === undefined) {
      return;
    }

    setIsOpen(open);
  }, [open]);

  React.useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  const context = React.useMemo(
    () => ({
      isOpen,
      setIsOpen,
      handleOnOpenChange,
      triggerRef,
      contentRef,
      getTriggerHoverProps,
      getContentHoverProps,
    }),
    [isOpen, handleOnOpenChange, getTriggerHoverProps, getContentHoverProps],
  );

  return (
    <PopoverContext.Provider value={context}>
      <PopoverPrimitive.Root
        data-slot="popover"
        {...native}
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={handleOnOpenChange}
      />
    </PopoverContext.Provider>
  );
}

Popover.displayName = "Popover";

export default Popover;
